import { useMemo } from 'react';
import { apiHooks } from '../../../../hooks/useRestAPIHooks';
import { ImpressionItem } from '../AITools/ImpressionEditor/ImpressionItem';

// FHIR references may be relative ("ClinicalImpression/{id}") or absolute
// ("https://.../ClinicalImpression/{id}") — match on the resource type name
// rather than a fixed-index split so both forms resolve to the same id.
const CLINICAL_IMPRESSION_REFERENCE_ID = /ClinicalImpression\/([^/]+)$/;

const getSupportingInfoIds = (carePlan?: fhir3.CarePlan): string[] =>
  (carePlan?.supportingInfo ?? [])
    .map(info => info.reference?.match(CLINICAL_IMPRESSION_REFERENCE_ID)?.[1])
    .filter((id): id is string => !!id);

/**
 * Fetches the `ClinicalImpression` sections referenced by a composite-report
 * CarePlan's `supportingInfo`, returned in `supportingInfo` order (CR-9/CR-10).
 *
 * Requests server-side ordering via `_id-sequence`, then defensively re-sorts
 * client-side in case the server does not honor it — this keeps section order
 * correct (matching the CRD) regardless of server behavior.
 */
export const useCompositeReportSections = (carePlan?: fhir3.CarePlan) => {
  const ids = useMemo(() => getSupportingInfoIds(carePlan), [carePlan]);

  const query = apiHooks.useAPIQuery(
    'GET /v1/fhir/dstu3/ClinicalImpression',
    {
      _tag: undefined,
      _id: ids.join(','),
      '_id-sequence': true,
    },
    { enabled: ids.length > 0 },
  );

  const sections = useMemo((): ImpressionItem[] => {
    const entries = query.data?.entry ?? [];
    const byId = new Map(
      (entries as Array<{ resource?: fhir.ClinicalImpression }>)
        .map(entry => entry.resource)
        .filter((r): r is fhir.ClinicalImpression => !!r?.id)
        .map(resource => [resource.id as string, resource] as const),
    );
    return ids
      .map(id => byId.get(id))
      .filter((r): r is fhir.ClinicalImpression => !!r)
      .map(impression => new ImpressionItem(impression));
  }, [query.data, ids]);

  return {
    sections,
    loading: ids.length > 0 && query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
